import React, { useState } from 'react';
import { AlertTriangle, RefreshCw, Settings, Loader2, CheckCircle2 } from 'lucide-react';
import { UserProfile } from '../types';
import { reconnectGoogle } from '../lib/googleCalendar';
import { useTranslation } from '../lib/translations';
import { useNavigate } from 'react-router-dom';

interface SyncStatusBannerProps {
  profile: UserProfile | null;
  onReconnectSuccess?: () => void;
}

const SyncStatusBanner: React.FC<SyncStatusBannerProps> = ({ profile, onReconnectSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  if (!profile || profile.role !== 'teacher') return null;

  const hasToken = !!sessionStorage.getItem('google_access_token');
  const syncDisconnected = profile.googleCalendarId && !hasToken;
  const syncNotConfigured = !profile.googleCalendarId;

  if (!syncDisconnected && !syncNotConfigured) return null;

  const handleReconnect = async () => {
    setIsReconnecting(true);
    try {
      const token = await reconnectGoogle();
      if (token) {
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          if (onReconnectSuccess) onReconnectSuccess();
        }, 2000);
      }
    } catch (e) {
      console.error(e);
    }
    setIsReconnecting(false);
  };

  if (showSuccess) {
    return (
      <div className="p-4 rounded-xl border border-teal-200 dark:border-teal-500/30 bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 flex items-center gap-3 animate-in fade-in duration-500">
        <CheckCircle2 size={20} className="animate-bounce" />
        <p className="text-xs font-black uppercase tracking-widest">{t.common.success}! {t.profile.syncEnabled}</p>
      </div>
    );
  }

  return (
    <div className={`p-4 rounded-xl border flex flex-col md:flex-row items-center justify-between gap-4 animate-in slide-in-from-top-4 duration-500 ${
      syncDisconnected 
      ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-500/30 text-amber-600 dark:text-amber-400' 
      : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400'
    }`}>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${syncDisconnected ? 'bg-amber-100 dark:bg-amber-500/20' : 'bg-slate-100 dark:bg-white/10'}`}>
          <AlertTriangle size={20} />
        </div>
        <p className="text-xs font-bold leading-relaxed">
          {syncDisconnected ? t.planner.syncWarning.expired : t.planner.syncWarning.notConfigured}
        </p>
      </div>
      <div className="flex items-center gap-2">
        {syncDisconnected ? (
          <button 
            onClick={handleReconnect}
            disabled={isReconnecting}
            className="flex items-center gap-2 px-6 py-2 rounded-lg font-black text-[10px] uppercase tracking-widest bg-amber-600 text-white hover:bg-amber-700 shadow-lg shadow-amber-500/20 transition-all active:scale-95 disabled:opacity-50"
          >
            {isReconnecting ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} 
            {t.planner.syncWarning.reconnectAction}
          </button>
        ) : (
          <button 
            onClick={() => navigate('/profile')}
            className="flex items-center gap-2 px-6 py-2 rounded-lg font-black text-[10px] uppercase tracking-widest bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:opacity-90 transition-all shadow-md"
          >
            <Settings size={14} /> {t.planner.syncWarning.configureAction}
          </button>
        )}
      </div>
    </div>
  );
};

export default SyncStatusBanner;